import { z } from "zod";
import { zPhoneNumber } from "../validations/phone-number-validator";
import { AgentHoursSchema, CustomPrepTimeHoursSchema } from "../modules/hours";
import { MenuConfigFormSchema } from "../modules/menu";
import { AgentType } from "@/types/base";

const PaymentModuleConfigSchema = z.union([
  z.object({
    provider: z.literal("none"),
  }),
  z.object({
    provider: z.literal("stripe"),
    strategy: z.literal("required"),
    requireTip: z.boolean(),
  }),
]);

export const BaseAgentFormSchema = z.object({
  // core
  id: z.string().optional(),
  name: z.string().trim().min(1, "Name is required."),
  type: z.enum(Object.values(AgentType) as [AgentType, ...AgentType[]]),
  placeId: z.string(),
  timezone: z.string().nullable(),
  hours: AgentHoursSchema.nullable(),
  hoursOrdering: AgentHoursSchema.nullable().optional(),
  accountId: z.string().min(1, "Organization is required").optional(),
  phoneNumber: zPhoneNumber.optional(),

  // twilio
  twilioOverride: z
    .object({
      enabled: z.boolean(),
      sid: z.string().optional(),
      auth: z.string().optional(),
    })
    .nullable()
    .optional(),
  smsPhoneNumber: zPhoneNumber.nullable().optional(),

  // direct prompting
  greetingMessageOpen: z
    .string()
    .trim()
    .min(1, "Greeting message is required."),
  greetingMessageOrderingClosed: z
    .string()
    .trim()
    .min(1, "Greeting message is required."),
  greetingMessageClosed: z
    .string()
    .trim()
    .min(1, "Greeting message is required."),
  prompt: z.string().nullable().optional(),

  // action triggers
  fallbackPhoneNumber: zPhoneNumber.nullable().optional(),
  transfers: z
    .array(
      z
        .object({
          key: z.string(),
          value: z.union([z.string(), z.array(z.string())]),
        })
        .refine(
          (obj) => {
            if (!obj.key && !obj.value) return true;
            if (!obj.key.trim()) return false;
            if (Array.isArray(obj.value)) {
              return obj.value.every((v) => zPhoneNumber.safeParse(v).success);
            }
            return zPhoneNumber.safeParse(obj.value).success;
          },
          { message: "Please enter a valid trigger and phone number" },
        ),
    )
    .optional(),
  messages: z
    .array(
      z
        .object({
          key: z.string(),
          value: z.string(),
        })
        .refine(
          (obj) => {
            if (!obj?.key && !obj?.value) return true;
            if (!obj?.key?.trim()) return false;
            return z.string().url().safeParse(obj?.value).success;
          },
          { message: "Please enter a valid trigger and URL" },
        ),
    )
    .optional(),

  // geographic consent
  twoPartyConsent: z.boolean().nullable(),

  streamConnectedAt: z.string().nullable().optional(),

  // POS Providers
  pos: z
    .enum([
      "toast",
      "square",
      "clover",
      "stream",
      "kwick",
      "loman",
      "spotOn",
      "foodtec",
    ])
    .nullable()
    .optional(),

  // Toast
  toastUrl: z.string().nullable().optional(),
  // Square
  squareMerchantId: z.string().nullable().optional(),
  squareLocationId: z.string().nullable().optional(),
  squareAccessToken: z.string().nullable().optional(),
  squareRefreshToken: z.string().nullable().optional(),
  // Clover
  cloverMerchantId: z.string().nullable().optional(),
  cloverAccessToken: z.string().nullable().optional(),
  cloverRefreshToken: z.string().nullable().optional(),
  cloverRefreshTokenExpiration: z.number().nullable().optional(),
  cloverOrderTypeId: z.string().nullable().optional(),
  // Kwick
  kwickMerchantKey: z.string().nullable().optional(),
  kwickMerchantToken: z.string().nullable().optional(),
  // Stream
  subprovider: z.enum(["toast", "clover"]).nullable().optional(),
  locationId: z.string().nullable().optional(),
  // SpotOn
  spotOnLocationId: z.string().nullable().optional(),
  // Foodtec
  foodtecBaseUrl: z.string().nullable().optional(),
  foodtecMenuAuth: z.string().nullable().optional(),
  foodtecCheckoutAuth: z.string().nullable().optional(),
  foodtecOrderAuth: z.string().nullable().optional(),
  foodtecOrderTypes: z.array(z.string()).nullable().optional(),

  // Menu
  menuModuleConfig: MenuConfigFormSchema.nullable().optional(),

  // Ordering
  orderingOrderUntilClosed: z.boolean().nullable().optional(),
  orderingAllowScheduledOrders: z
    .enum(["always", "never", "normal", "morning"])
    .nullable()
    .optional(),
  orderingShouldTotal: z.boolean().optional(),
  orderingExactTimeEstimate: z.boolean().optional(),
  orderingInstantSubmit: z.boolean().nullable().optional(),
  orderingMinLeadTime: z.number().nullable().optional(),
  orderingMinTotalThreshold: z.number().nullable().optional(),
  orderingMaxTotalThreshold: z.number().nullable().optional(),
  // Toast Ordering
  orderingDynamicPrepTime: z.boolean().nullable().optional(),
  // [Toast, Loman] Ordering
  orderingTaxRate: z.number().nullable().optional(),
  // [Stream, Foodtec] Ordering
  useOrderEntryTool: z.boolean().nullable().optional(),

  // Prep times
  pickupPrepTime: z.number().min(10).nullable().optional(),
  deliveryPrepTime: z.number().min(10).nullable().optional(),
  customPrepTimeEnabled: z.boolean().optional(),
  customPrepTimeHours: CustomPrepTimeHoursSchema.optional(),

  // Pickup
  pickupEnabled: z.boolean().nullable().optional(),
  pickupPaymentModuleConfig: PaymentModuleConfigSchema.optional(),

  // Delivery
  deliveryEnabled: z.boolean().nullable().optional(),
  deliveryPaymentModuleConfig: PaymentModuleConfigSchema.optional(),
  deliveryVerifyAddress: z.boolean().nullable().optional(),
  deliveryLatitude: z.number().min(-90).max(90).nullable().optional(),
  deliveryLongitude: z.number().min(-180).max(180).nullable().optional(),
  deliveryFee: z.number().nullable().optional(),
  deliveryRadius: z
    .number()
    .min(0.0, "Please set a delivery radius")
    .nullable()
    .optional(),
  deliveryRegionCode: z.string().nullable().optional(),
  deliveryWaiveMinimum: z.number().min(0).nullable().optional(),

  // Reservation
  reservationProvider: z.enum(["resos"]).nullable().optional(),
  reservationAccessToken: z.string().nullable().optional(),
  reservationUrl: z.string().nullable().optional(),
  reservationEnabled: z.boolean().nullable().optional(),
  reservationSlotDuration: z.number().nullable().optional(),
  reservationMaxPartySize: z.number().nullable().optional(),

  // voicemail
  voicemailEmail: z.string().nullable().optional(),
});
